
import { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Plus, FolderPlus, Check, Folder } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { getPromptCollections, createCollection, addPromptToCollection } from "@/services/savedPromptService";
import type { PromptCollection } from "@/services/savedPromptService";

interface CollectionSelectorModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  promptId: string;
  promptTitle: string;
  onSuccess?: () => void;
  embedded?: boolean;
}

const CollectionSelectorModal = ({
  open,
  onOpenChange,
  promptId,
  promptTitle,
  onSuccess,
  embedded = false
}: CollectionSelectorModalProps) => {
  const [collections, setCollections] = useState<PromptCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (embedded || open) {
      loadCollections();
      setSelectedCollections([]);
    }
  }, [open, embedded]);

  const loadCollections = async () => {
    setIsLoading(true);
    try {
      const collections = await getPromptCollections();
      console.log("Loaded collections:", collections.length);
      setCollections(collections);
    } catch (error) {
      console.error("Error loading collections:", error);
      toast.error("Failed to load your collections");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCollectionToggle = (e: React.MouseEvent, collectionId: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    setSelectedCollections(prev => 
      prev.includes(collectionId) 
        ? prev.filter(id => id !== collectionId) 
        : [...prev, collectionId]
    );
  };

  const handleCreateCollection = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!newCollectionName.trim()) {
      toast.error("Please enter a name for your new collection");
      return;
    }

    setIsCreatingCollection(true);
    try {
      const newCollection = await createCollection(
        newCollectionName, 
        newCollectionDescription || undefined
      );
      
      if (newCollection) {
        toast.success("Collection created");
        setCollections(prev => [...prev, newCollection]);
        setSelectedCollections(prev => [...prev, newCollection.id]);
        setNewCollectionName("");
        setNewCollectionDescription("");
        setIsCreatingCollection(false);
      }
    } catch (error) {
      console.error("Error creating collection:", error);
      toast.error("Failed to create collection");
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleSavePrompt = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (selectedCollections.length === 0) {
      toast.error("Please select at least one collection");
      return;
    }

    setIsSaving(true);
    console.log(`Saving prompt ${promptId} to ${selectedCollections.length} collections`);
    
    try {
      for (const collectionId of selectedCollections) {
        console.log(`Processing collection: ${collectionId}`);
        const result = await addPromptToCollection(promptId, collectionId);
        if (!result) {
          console.error(`Failed to add to collection ${collectionId}`);
        }
      }
      
      toast.success(`Prompt saved to ${selectedCollections.length > 1 ? 'collections' : 'collection'}`);
      if (onSuccess) onSuccess();
      if (!embedded && onOpenChange) onOpenChange(false);
    } catch (error) {
      console.error("Error saving to collections:", error);
      toast.error("Failed to save to collections");
    } finally {
      setIsSaving(false);
    }
  };

  // Add data-stop-propagation attribute to the outer div to prevent card navigation
  const content = (
    <div data-stop-propagation="true">
      <div className={embedded ? "" : "py-4"}>
        {isLoading ? (
          <div className="py-6 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {collections.length > 0 ? (
              <>
                <p className="text-sm font-medium mb-2">Select collections to add this prompt to:</p>
                <ScrollArea className="max-h-[300px] overflow-y-auto">
                  <div className="grid grid-cols-1 gap-2 pr-4 pb-2">
                    {collections.map((collection) => (
                      <div 
                        key={collection.id} 
                        className={`
                          flex items-center p-3 rounded-md border
                          ${selectedCollections.includes(collection.id) 
                            ? "border-primary bg-primary/5" 
                            : "border-input hover:bg-accent hover:text-accent-foreground"}
                          cursor-pointer transition-colors
                        `}
                        onClick={(e) => handleCollectionToggle(e, collection.id)}
                      >
                        <Checkbox
                          id={`collection-${collection.id}`}
                          checked={selectedCollections.includes(collection.id)}
                          onCheckedChange={() => {}}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`collection-${collection.id}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {collection.name}
                          </Label>
                          {collection.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {collection.description}
                            </p>
                          )}
                          {collection.prompts_count !== undefined && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {collection.prompts_count} prompt{collection.prompts_count !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>You don't have any collections yet.</p>
                <p className="text-sm mt-1">Create your first collection below.</p>
              </div>
            )}
            
            <Separator className="my-4" />
            
            {isCreatingCollection ? (
              <div className="space-y-3 mt-4 p-4 border rounded-md bg-muted/30">
                <div className="grid gap-2">
                  <Label htmlFor="new-collection-name">Collection Name</Label>
                  <Input
                    id="new-collection-name"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="My Collection"
                    autoFocus
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="new-collection-description">Description (optional)</Label>
                  <Input
                    id="new-collection-description"
                    value={newCollectionDescription}
                    onChange={(e) => setNewCollectionDescription(e.target.value)}
                    placeholder="Description"
                  />
                </div>
                <div className="flex items-center justify-end space-x-2 mt-4" onClick={e => e.stopPropagation()}>
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsCreatingCollection(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleCreateCollection}
                    disabled={!newCollectionName.trim()}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Create
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsCreatingCollection(true);
                }}
                variant="outline" 
                className="w-full"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                Create New Collection
              </Button>
            )}
          </div>
        )}
      </div>
      
      {embedded ? (
        <Button 
          className="w-full mt-4" 
          disabled={selectedCollections.length === 0 || isSaving}
          onClick={handleSavePrompt}
        >
          {isSaving ? 'Saving...' : `Save to ${selectedCollections.length} Collection${selectedCollections.length !== 1 ? 's' : ''}`}
        </Button>
      ) : (
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onOpenChange) onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button 
            disabled={selectedCollections.length === 0 || isSaving}
            onClick={handleSavePrompt}
          >
            {isSaving ? 'Saving...' : 'Save to Collections'}
          </Button>
        </DialogFooter>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog 
      open={open} 
      onOpenChange={(open) => {
        if (onOpenChange) onOpenChange(open);
      }}
    >
      <DialogContent className="sm:max-w-md" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Save to Collections</DialogTitle>
          <DialogDescription>
            Add "{promptTitle}" to one or more collections for easy access.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default CollectionSelectorModal;
